import { useToast } from "@chakra-ui/react";
import { isStandstillExemptionRefusal } from "./standstillExemptionRefusal";
import { isRejectedTenderRefusal } from "./rejectedTenderRefusal";
import { useAppDispatch } from "../../../common/redux/hooks";
import { isFlaggedEntriesRefusal } from "../designContest/flaggedEntriesRefusal";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BidStatus,
  Currency,
  SourcingEventStatus,
  StandstillExemption,
  useAwardSourcingEventMutation,
  useGetEvaluationQuery,
  useListBidsForEventQuery,
  type ReceivedBidDto,
  type EvalBidDto,
  type EvaluationDto,
  type NegotiationRoundDto,
  type SourcingEventDto,
  bffApi,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { JustificationModal } from "../rounds/JustificationModal";
import { awardingDepartsFromPlan } from "../rounds/partIIDialogue";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { basketBidIds } from "./basketBidIds";
import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { useCurrentMoment } from "../../sourcing-events/portal/utils/useCurrentMoment";
import { canSelectWinner } from "./canSelectWinner";
import { hasStandingAwardDecision } from "./hasStandingAwardDecision";
import { seedWinnerIds } from "./seedWinnerIds";
import { juryRankingDeparture, type JuryRankingDeparture } from "./juryRanking";
import { flaggedAmong } from "../designContest/jury";

interface AwardSelection {
  evaluation: EvaluationDto | undefined;
  isLoading: boolean;
  isError: boolean;
  reloadEvaluation: () => void;
  isReloadingEvaluation: boolean;
  winnerIds: string[];
  toggleWinner: (bidId: string) => void;
  internalNote: string;
  setInternalNote: (note: string) => void;
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  amount: string;
  setAmount: (amount: string) => void;
  buyerSetsStandstill: boolean;
  choosesVoluntaryStandstill: boolean;
  standstillDays: string;
  setStandstillDays: (days: string) => void;
  noStandstill: boolean;
  setNoStandstill: (noStandstill: boolean) => void;
  standstillExemption: StandstillExemption | null;
  setStandstillExemption: (exemption: StandstillExemption) => void;
  standstillProblem: StandstillProblem | null;
  minimumStandstillDays: number;
  ruledOutExemptions: ReadonlySet<StandstillExemption>;
  tenderCheck: TenderCheck;
  soleSupplierRefused: boolean;
  recheckTenders: () => void;
  juryRankingDeparture: JuryRankingDeparture | null;
  award: () => Promise<void>;
  isAwarding: boolean;
  refusedOverFlags: boolean;
  clearRefusedOverFlags: () => void;
}

export type TenderCheck = "CHECKED" | "CHECKING" | "FAILED";

export type StandstillProblem =
  | "DAYS_NOT_A_WHOLE_NUMBER_IN_RANGE"
  | "DAYS_BELOW_MINIMUM"
  | "EXEMPTION_MISSING";

export const MAX_STANDSTILL_DAYS = 3650;
export const MAX_VOLUNTARY_STANDSTILL_DAYS = 15;

const DEFAULT_STANDSTILL_DAYS = "10";

const NOTHING_TO_AWARD: AwardSelection = {
  evaluation: undefined,
  isLoading: false,
  isError: false,
  reloadEvaluation: () => undefined,
  isReloadingEvaluation: false,
  winnerIds: [],
  toggleWinner: () => undefined,
  internalNote: "",
  setInternalNote: () => undefined,
  currency: Currency.Nok,
  setCurrency: () => undefined,
  amount: "",
  setAmount: () => undefined,
  buyerSetsStandstill: false,
  choosesVoluntaryStandstill: false,
  standstillDays: DEFAULT_STANDSTILL_DAYS,
  setStandstillDays: () => undefined,
  noStandstill: false,
  setNoStandstill: () => undefined,
  standstillExemption: null,
  setStandstillExemption: () => undefined,
  standstillProblem: null,
  minimumStandstillDays: 1,
  ruledOutExemptions: new Set(),
  tenderCheck: "CHECKED",
  soleSupplierRefused: false,
  recheckTenders: () => undefined,
  juryRankingDeparture: null,
  award: () => Promise.resolve(),
  refusedOverFlags: false,
  clearRefusedOverFlags: () => undefined,
  isAwarding: false,
};

const AwardSelectionContext = createContext<AwardSelection>(NOTHING_TO_AWARD);

interface AwardForm {
  eventId: string;
  /** null while the seeded winners still stand; an array once the buyer has touched the ticks. */
  picked: string[] | null;
  internalNote: string;
  currency: Currency;
  amount: string;
  standstillDays: string;
  noStandstill: boolean;
  standstillExemption: StandstillExemption | null;
  soleSupplierRefusedFor: string | null;
}

const emptyForm = (event: SourcingEventDto): AwardForm => {
  const estimated =
    event.eventContext.type === "Public"
      ? event.eventContext.estimatedValue
      : null;
  return {
    eventId: event.id,
    picked: null,
    internalNote: "",
    currency: estimated?.currency ?? Currency.Nok,
    amount:
      estimated?.value != null && !eventIsDesignContest(event)
        ? String(estimated.value)
        : "",
    standstillDays: event.choosesVoluntaryStandstill
      ? "0"
      : DEFAULT_STANDSTILL_DAYS,
    noStandstill: false,
    standstillExemption: null,
    soleSupplierRefusedFor: null,
  };
};

// FOA § 25-2 (2) a / forsyningsforskriften § 21-2 (2) a: none is required, so 0 is the default.
const voluntaryStandstillProblemOf = (
  form: AwardForm,
): StandstillProblem | null => {
  const days = form.standstillDays.trim();
  const whole = /^\d+$/.test(days) ? Number(days) : NaN;
  return whole >= 0 && whole <= MAX_VOLUNTARY_STANDSTILL_DAYS
    ? null
    : "DAYS_NOT_A_WHOLE_NUMBER_IN_RANGE";
};

const standstillProblemOf = (
  form: AwardForm,
  ruledOut: ReadonlySet<StandstillExemption>,
  minimumDays: number,
): StandstillProblem | null => {
  if (form.noStandstill) {
    return form.standstillExemption == null ||
      ruledOut.has(form.standstillExemption)
      ? "EXEMPTION_MISSING"
      : null;
  }
  const days = form.standstillDays.trim();
  const whole = /^\d+$/.test(days) ? Number(days) : NaN;
  if (!(whole >= 1 && whole <= MAX_STANDSTILL_DAYS))
    return "DAYS_NOT_A_WHOLE_NUMBER_IN_RANGE";
  return whole < minimumDays ? "DAYS_BELOW_MINIMUM" : null;
};

// FOA § 10-2 (3): b only when no other supplier tendered, c only for a call-off under a framework agreement.
const exemptionsRuledOut = (
  deliveredBids: readonly ReceivedBidDto[] | undefined,
  winnerIds: string[],
  soleSupplierRefused: boolean,
): ReadonlySet<StandstillExemption> => {
  const othersTendered =
    deliveredBids === undefined ||
    othersDeliveredAnyTender(deliveredBids, winnerIds);
  const moreThanOneWinner = winnerIds.length > 1;
  return new Set([
    ...(othersTendered || moreThanOneWinner || soleSupplierRefused
      ? [StandstillExemption.SoleConcernedSupplier]
      : []),
    StandstillExemption.FrameworkAgreementCallOff,
  ]);
};

// Mirrors SourcingEventService.soleConcernedSupplier: every round counts, not only the one being awarded.
const othersDeliveredAnyTender = (
  deliveredBids: readonly ReceivedBidDto[],
  winnerIds: string[],
): boolean => {
  const delivered = deliveredBids.filter(
    (bid) => bid.status !== BidStatus.Draft,
  );
  const winners = new Set(
    delivered
      .filter((bid) => winnerIds.includes(bid.id))
      .map((bid) => bid.owner),
  );
  return delivered.some((bid) => !winners.has(bid.owner));
};

const standstillRequest = (form: AwardForm) =>
  form.noStandstill
    ? { standstillExemption: form.standstillExemption }
    : { standstillDays: Number(form.standstillDays.trim()) };

const collectsAward = (
  event: SourcingEventDto,
  rounds: readonly NegotiationRoundDto[],
  now: number,
): boolean =>
  (event.status === SourcingEventStatus.Evaluation ||
    event.status === SourcingEventStatus.Evaluated) &&
  canSelectWinner(event, rounds, now) &&
  !hasStandingAwardDecision(event);

const REJECTED_TENDER_TOAST = "award-rejected-tender";

export const AwardSelectionProvider = ({
  event,
  children,
}: {
  event: SourcingEventDto;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { trigger } = useApiError();
  const dispatch = useAppDispatch();
  const [refusedOverFlags, setRefusedOverFlags] = useState(false);
  const [rejectedTenderRefusal, setRejectedTenderRefusal] = useState<{
    chosen: EvalBidDto[];
    refusedAt: number;
  } | null>(null);
  const { rounds, latestSentRound } = useRoundSelection();
  const now = useCurrentMoment().valueOf();
  const {
    currentData: evaluation,
    isLoading: isFirstLoad,
    isError,
    isFetching: isReloadingEvaluation,
    fulfilledTimeStamp: evaluationFetchedAt,
    refetch: reloadEvaluation,
  } = useGetEvaluationQuery(
    collectsAward(event, rounds, now)
      ? { eventId: event.id, round: latestSentRound?.id }
      : skipToken,
  );
  const isLoading =
    isFirstLoad || (evaluation === undefined && isReloadingEvaluation);
  const {
    data: deliveredBidsData,
    isError: tenderCheckFailed,
    refetch: recheckTenders,
  } = useListBidsForEventQuery(
    collectsAward(event, rounds, now) ? { eventId: event.id } : skipToken,
  );
  const [awardEvent, { isLoading: isAwarding }] =
    useAwardSourcingEventMutation();

  // React Router reuses SourcingEventView across a :eventId change, so the form has to notice.
  const [form, setForm] = useState(() => emptyForm(event));
  if (form.eventId !== event.id) setForm(emptyForm(event));
  const { picked, internalNote, currency, amount } = form;
  const buyerSetsStandstill = event.buyerSetsStandstill ?? false;
  const choosesVoluntaryStandstill = event.choosesVoluntaryStandstill ?? false;

  const awardable = new Set(
    (evaluation?.bids ?? [])
      .filter((bid) => bid.status !== BidStatus.Rejected)
      .map((bid) => bid.bidId),
  );
  const seeded = evaluation
    ? seedWinnerIds(evaluation, basketBidIds(event, latestSentRound))
    : [];
  const winnerIds = (picked ?? seeded).filter((id) => awardable.has(id));
  const winnersKey = [...winnerIds].sort().join(",");
  const soleSupplierRefused = form.soleSupplierRefusedFor === winnersKey;
  const checkedBids = tenderCheckFailed ? undefined : deliveredBidsData?.bids;
  const ruledOutExemptions = exemptionsRuledOut(
    checkedBids,
    winnerIds,
    soleSupplierRefused,
  );
  const tenderCheck: TenderCheck = tenderCheckFailed
    ? "FAILED"
    : deliveredBidsData
      ? "CHECKED"
      : "CHECKING";
  const minimumStandstillDays = event.minimumStandstillDays ?? 1;
  const standstillProblem = choosesVoluntaryStandstill
    ? voluntaryStandstillProblemOf(form)
    : buyerSetsStandstill
      ? standstillProblemOf(form, ruledOutExemptions, minimumStandstillDays)
      : null;

  const change = (fields: Partial<AwardForm>) =>
    setForm((previous) => ({ ...previous, ...fields }));

  const toggleWinner = (bidId: string) =>
    change({
      picked: winnerIds.includes(bidId)
        ? winnerIds.filter((id) => id !== bidId)
        : [...winnerIds, bidId],
    });

  const [asksForDepartureJustification, setAsksForDepartureJustification] =
    useState(false);
  const [asksForJuryDepartureReason, setAsksForJuryDepartureReason] =
    useState(false);
  const rejectedIds = new Set(
    (evaluation?.bids ?? [])
      .filter((bid) => bid.status === BidStatus.Rejected)
      .map((bid) => bid.bidId),
  );
  const departureFromJury = juryRankingDeparture(event, winnerIds, rejectedIds);

  const send = async ({
    dialogueDepartureJustification,
    juryDepartureJustification,
  }: {
    dialogueDepartureJustification?: string;
    juryDepartureJustification?: string;
  } = {}) => {
    if (winnerIds.length === 0) return;
    const parsed = amount.trim() === "" ? null : Number(amount);
    try {
      await awardEvent({
        eventId: event.id,
        awardSourcingEventRequest: {
          winningBidIds: winnerIds,
          internalNote: internalNote.trim() || null,
          eventValue:
            parsed != null && !Number.isNaN(parsed)
              ? { value: parsed, currency }
              : null,
          dialogueDepartureJustification,
          juryDepartureJustification,
          ...(flaggedAmong(
            event.eventContext.type === "Public"
              ? event.eventContext.designContest
              : null,
            winnerIds,
          ).length > 0
            ? { proceedDespiteFlaggedEntries: true }
            : {}),
          ...(buyerSetsStandstill ? standstillRequest(form) : {}),
          ...(choosesVoluntaryStandstill
            ? { standstillDays: Number(form.standstillDays.trim()) }
            : {}),
        },
      }).unwrap();
      setAsksForDepartureJustification(false);
      setAsksForJuryDepartureReason(false);
      toast({
        title: t("Event awarded"),
        status: "success",
        position: "top-right",
      });
    } catch (e) {
      if (isFlaggedEntriesRefusal(e)) {
        setAsksForDepartureJustification(false);
        setAsksForJuryDepartureReason(false);
        setRefusedOverFlags(true);
        dispatch(bffApi.util.invalidateTags(["SourcingEvent"]));
        return;
      }
      if (isRejectedTenderRefusal(e)) {
        setAsksForDepartureJustification(false);
        setAsksForJuryDepartureReason(false);
        sayWhichTenderWasRejected();
        return;
      }
      if (isStandstillExemptionRefusal(e)) {
        if (
          form.standstillExemption === StandstillExemption.SoleConcernedSupplier
        ) {
          change({ soleSupplierRefusedFor: winnersKey });
        }
        recheckTenders();
      }
      trigger(e);
    }
  };

  const sayWhichTenderWasRejected = () => {
    setRejectedTenderRefusal({
      chosen: (evaluation?.bids ?? []).filter((bid) =>
        winnerIds.includes(bid.bidId),
      ),
      refusedAt: Date.now(),
    });
    reloadEvaluation();
  };

  useEffect(() => {
    if (!rejectedTenderRefusal || isReloadingEvaluation) return;
    const reloaded =
      evaluation !== undefined &&
      (evaluationFetchedAt ?? 0) >= rejectedTenderRefusal.refusedAt;
    if (!reloaded && !isError) return;
    setRejectedTenderRefusal(null);
    const { chosen } = rejectedTenderRefusal;
    const stillAwardable = new Set(
      (reloaded ? evaluation.bids : [])
        .filter((bid) => bid.status !== BidStatus.Rejected)
        .map((bid) => bid.bidId),
    );
    const rejectedNames = reloaded
      ? chosen
          .filter((bid) => !stillAwardable.has(bid.bidId))
          .map((bid) => bid.supplierName)
      : [];
    if (reloaded)
      change({
        picked: chosen
          .map((bid) => bid.bidId)
          .filter((id) => stillAwardable.has(id)),
      });
    if (toast.isActive(REJECTED_TENDER_TOAST)) return;
    toast({
      id: REJECTED_TENDER_TOAST,
      status: "error",
      position: "top-right",
      title:
        rejectedNames.length > 0
          ? t(
              "The tender from {{suppliers}} has been rejected and can't be awarded. Choose another tenderer.",
              {
                suppliers: rejectedNames.join(", "),
              },
            )
          : t(
              "A tender you chose has been rejected and can't be awarded. Choose another tenderer.",
            ),
    });
  }, [
    rejectedTenderRefusal,
    isReloadingEvaluation,
    evaluation,
    evaluationFetchedAt,
    isError,
    toast,
    t,
  ]);

  const award = async () => {
    if (winnerIds.length === 0 || standstillProblem != null) return;
    if (departureFromJury === "REFUSED_BY_BINDING_JURY") return;
    if (departureFromJury === "NEEDS_REASON") {
      setAsksForJuryDepartureReason(true);
      return;
    }
    if (awardingDepartsFromPlan(event, rounds)) {
      setAsksForDepartureJustification(true);
      return;
    }
    await send();
  };

  return (
    <AwardSelectionContext.Provider
      value={{
        evaluation,
        isLoading,
        isError,
        reloadEvaluation,
        isReloadingEvaluation,
        winnerIds,
        toggleWinner,
        internalNote,
        setInternalNote: (note) => change({ internalNote: note }),
        currency,
        setCurrency: (next) => change({ currency: next }),
        amount,
        setAmount: (next) => change({ amount: next }),
        buyerSetsStandstill,
        choosesVoluntaryStandstill,
        standstillDays: form.standstillDays,
        setStandstillDays: (days) => change({ standstillDays: days }),
        noStandstill: choosesVoluntaryStandstill
          ? Number(form.standstillDays.trim()) === 0
          : form.noStandstill,
        setNoStandstill: (next) => change({ noStandstill: next }),
        standstillExemption: form.standstillExemption,
        setStandstillExemption: (exemption) =>
          change({ standstillExemption: exemption }),
        standstillProblem,
        minimumStandstillDays,
        ruledOutExemptions,
        tenderCheck,
        soleSupplierRefused:
          soleSupplierRefused &&
          checkedBids !== undefined &&
          !othersDeliveredAnyTender(checkedBids, winnerIds),
        recheckTenders: () => void recheckTenders(),
        juryRankingDeparture: departureFromJury,
        award,
        refusedOverFlags,
        clearRefusedOverFlags: () => setRefusedOverFlags(false),
        isAwarding,
      }}
    >
      {children}
      <JustificationModal
        isOpen={asksForDepartureJustification}
        title={t("Award without the planned dialogue")}
        description={t(
          "The procurement documents said dialogue was planned. Explain why you award without it (FOA § 10-5 (2) h).",
        )}
        label={t("Why the award is made without dialogue")}
        confirmLabel={t("Award without dialogue")}
        isLoading={isAwarding}
        onConfirm={(justification) =>
          void send({ dialogueDepartureJustification: justification })
        }
        onClose={() => setAsksForDepartureJustification(false)}
      />
      <JustificationModal
        isOpen={asksForJuryDepartureReason}
        title={t("Choose winners other than the jury's ranking")}
        description={t(
          "The jury only gives an opinion, so you may depart from its ranking. Explain why. The reason goes into the protocol.",
        )}
        label={t("Why you depart from the jury's ranking")}
        confirmLabel={t("Choose these winners")}
        isLoading={isAwarding}
        onConfirm={(justification) =>
          void send({ juryDepartureJustification: justification })
        }
        onClose={() => setAsksForJuryDepartureReason(false)}
      />
    </AwardSelectionContext.Provider>
  );
};

export const useAwardSelection = (): AwardSelection =>
  useContext(AwardSelectionContext);
